// Tiny home-rolled i18n — no npm package (would be overkill for ~80 keys).
//
// Usage:
//   import { useT, setLocale } from "@/lib/i18n";
//   const t = useT();
//   <h1>{t("nav.home")}</h1>
//   setLocale("ru");
//
// On first load, the locale is detected from (in priority order):
//   1. localStorage `taklifla.locale` if user previously chose
//   2. <html lang> attribute if a server-side stub injected it
//   3. navigator.language prefix match (uz/ru/en)
//   4. fallback to "uz" (primary market)
//
// Components call useT() to subscribe; non-component callers can use the
// bare t() export but won't auto-rerender.

import { useEffect, useState } from "react";
import uz from "./i18n.uz";
import ru from "./i18n.ru";
import en from "./i18n.en";
import { isIntlSite } from "./market";
import { tkDictionaries } from "./i18n-tk";

export type Locale = "uz" | "ru" | "en";
export const SUPPORTED_LOCALES: ReadonlyArray<Locale> = ["uz", "ru", "en"];

const KEY = "taklifla.locale";

const dictionaries: Record<Locale, Record<string, string>> = {
  uz: { ...uz, ...tkDictionaries.uz },
  ru: { ...ru, ...tkDictionaries.ru },
  en: { ...en, ...tkDictionaries.en },
};

function detectInitial(): Locale {
  if (typeof window === "undefined") return "uz";
  // An explicit URL override wins over everything, on either host. This is
  // what lets a marketing link (e.g. a Russian Google Ads ad → a Russian
  // landing page) force the locale regardless of the visitor's stored
  // choice or the host default: `taklifla.uz/?lang=ru` opens in Russian.
  // We accept `lang` and Google's `hl` param name; the value is validated
  // against SUPPORTED_LOCALES. It sets the in-memory locale for the whole
  // SPA session (client-side navigation never reloads, so it sticks as the
  // visitor browses) but is intentionally NOT written to localStorage — a
  // returning visitor's own saved choice still wins on their next organic
  // visit; the ad link only forces the language for that landing session.
  try {
    const params = new URLSearchParams(window.location.search);
    const q = (params.get("lang") ?? params.get("hl") ?? "").toLowerCase();
    if (q && SUPPORTED_LOCALES.includes(q as Locale)) {
      return q as Locale;
    }
  } catch { /* URL unparseable */ }
  // An explicit prior choice always wins, on either host — browser
  // language detection is intentionally skipped either way (a Russian-
  // browser user in Tashkent should still land in Uzbek by default and
  // pick RU/EN themselves if they want; an English-browser visitor on
  // .com who already switched to Uzbek should stay in Uzbek).
  try {
    const stored = localStorage.getItem(KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
      return stored as Locale;
    }
  } catch { /* localStorage unavailable */ }
  // No stored choice: the product is Uzbek-first on taklifla.uz (primary
  // market) — first-time visitors there ALWAYS see Uzbek. taklifla.com is
  // the English-first international mirror of the SAME deployment, so
  // first-time visitors there default to English instead. This is a
  // host check, not browser-language detection, and only changes the
  // *default* — nothing here touches .uz behavior when isIntlSite() is
  // false.
  if (isIntlSite()) return "en";
  return "uz";
}

let current: Locale = detectInitial();
const listeners = new Set<() => void>();

// Sync <html lang> on every locale change so screen readers and Google
// pick up the correct language.
function syncHtmlLang() {
  if (typeof document !== "undefined") {
    document.documentElement.lang = current;
  }
}
syncHtmlLang();

export function getLocale(): Locale {
  return current;
}

export function setLocale(l: Locale): void {
  if (!SUPPORTED_LOCALES.includes(l) || l === current) return;
  current = l;
  try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
  syncHtmlLang();
  listeners.forEach((fn) => fn());
}

export function onLocaleChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Look up a translation. Falls through: current locale → Uzbek (primary
 * dictionary) → fallback → key. Means a missing en/ru key won't show "key"
 * in the UI; the user sees Uzbek, which is at least sensible.
 */
export function t(key: string, fallback?: string): string {
  const v = dictionaries[current][key];
  if (v != null) return v;
  const uzv = dictionaries.uz[key];
  if (uzv != null) return uzv;
  return fallback ?? key;
}

/** Display name for a locale, in that locale. Used by the language picker. */
export function localeName(l: Locale): string {
  switch (l) {
    case "uz": return "O'zbekcha";
    case "ru": return "Русский";
    case "en": return "English";
  }
}

/** Short label (2 chars) used for compact toggles. */
export function localeShort(l: Locale): string {
  return l.toUpperCase();
}

// ── React hooks ────────────────────────────────────────────────────────

/** Returns [locale, setLocale]; component re-renders when locale changes. */
export function useLocale() {
  const [l, setL] = useState<Locale>(current);
  useEffect(
    () => onLocaleChange(() => { setL(getLocale()); }),
    [],
  );
  return [l, setLocale] as const;
}

/** Returns the bound t() function with re-render on locale change. */
export function useT() {
  useLocale();
  return t;
}
